import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Typography, Spin, Alert, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { refreshOnboarding } from '../../services/stripeService';

const { Title, Paragraph, Link } = Typography;

function StripeReauthPage() {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Get userType directly from URL params
  const searchParams = new URLSearchParams(location.search);
  const userType = searchParams.get('userType');
  const userId = searchParams.get(`${userType}_id`);

  // Derive dashboard URL based on user type
  const dashboardUrl = `/${userType}`;

  const attemptReauth = async () => {
    if (!userType) {
      setError('Missing user type in URL');
      setIsRedirecting(false);
      return;
    }

    if (!userId) {
      setError(`Missing ${userType} ID in URL`);
      setIsRedirecting(false);
      return;
    }

    if (!['borrower', 'investor'].includes(userType)) {
      setError('Invalid user type');
      setIsRedirecting(false);
      return;
    }

    try {
      setIsRedirecting(true);

      const { data, error: apiError } = await refreshOnboarding(userType, userId);

      if (apiError) {
        throw new Error(apiError);
      }

      if (data.onboardingUrl) {
        window.location.href = data.onboardingUrl;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err) {
      setError(err.message || 'Failed to reauthorize');
      setIsRedirecting(false);
    }
  };

  useEffect(() => {
    attemptReauth();
  }, [userId, userType]);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '48px 24px',
      }}
    >
      <div style={{ maxWidth: '460px', margin: '0 auto', width: '100%' }}>
        <Title level={2} style={{ textAlign: 'center' }}>
          Reconnecting to Stripe
        </Title>
        <Paragraph type="secondary" style={{ textAlign: 'center' }}>
          {isRedirecting
            ? 'Please wait while we redirect you to Stripe...'
            : 'We need to reconnect your account with Stripe.'}
        </Paragraph>
      </div>

      <Card style={{ maxWidth: '460px', margin: '32px auto 0' }}>
        {isRedirecting ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert message="Error reconnecting to Stripe" description={error} type="error" showIcon />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Link onClick={() => navigate(dashboardUrl)}>Return to dashboard</Link>
              <Button type="primary" icon={<ReloadOutlined />} onClick={attemptReauth}>
                Try again
              </Button>
            </div>
          </Space>
        ) : null}
      </Card>
    </div>
  );
}

export default StripeReauthPage;
